import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { MethodOption } from '@/api/resourceCapabilitiesApi'

/**
 * Choose the methods a process step belongs to.
 *
 * The mirror of `ProcessStepPicker` — that one picks steps for a method, this
 * one picks methods for a step — and both write the same membership. A
 * multi-select because a step can be covered by several methods.
 */
export function MethodPicker({
  options,
  value,
  onChange,
  disabled = false,
}: {
  options: MethodOption[]
  /** Selected method ids. */
  value: string[]
  onChange: (methodIds: string[]) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? options.filter(o => o.name.toLowerCase().includes(q)) : options
  }, [options, query])

  const selected = new Set(value)
  const names = options.filter(o => selected.has(o.id)).map(o => o.name)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          disabled={disabled}
          className='h-9 w-full justify-between font-normal'
        >
          {/* Names, not a count: one or two methods is the common case, and the
              name is the thing being decided. */}
          <span className={cn('truncate', names.length === 0 && 'text-muted-foreground')}>
            {names.length === 0 ? 'No method' : names.join(', ')}
          </span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[20rem] p-0' align='start'>
        {options.length > 6 && (
          <div className='relative border-b p-2'>
            <Search className='absolute left-4 top-4 h-3.5 w-3.5 text-muted-foreground' />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder='Search methods…'
              className='h-8 pl-7 text-sm'
            />
          </div>
        )}
        <div className='max-h-72 overflow-auto p-1'>
          {filtered.length === 0 ? (
            <p className='py-6 text-center text-sm text-muted-foreground'>No matching methods.</p>
          ) : (
            filtered.map(method => (
              <label
                key={method.id}
                className='flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent'
              >
                <Checkbox
                  checked={selected.has(method.id)}
                  onCheckedChange={() =>
                    onChange(
                      selected.has(method.id)
                        ? value.filter(id => id !== method.id)
                        : [...value, method.id],
                    )
                  }
                />
                <span className='flex-1'>{method.name}</span>
                {/* How many processes the method already covers — context for
                    whether this step belongs with them. */}
                <span className='shrink-0 text-xs text-muted-foreground'>
                  {method.stepCount}
                </span>
                {selected.has(method.id) && <Check className='h-3.5 w-3.5 text-primary' />}
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
